import type { TFunction } from 'i18next';
import type { Department } from '@bliks-io/contracts';
import type {
  EntityPickerDepartmentFilter,
  EntityPickerDepartmentFilterOption
} from '@bliks-io/ui';
import { getDepartmentPathNames } from '@/lib/department-utils';

/**
 * Convert flat departments (with materialized `path`) into a hierarchically
 * ordered, depth-annotated option list — parents render before their children
 * and `depth` drives indentation in the dropdown. `pathLabels` carries the
 * full root→leaf names so the trigger pill can render a breadcrumb.
 */
export function buildDepartmentOptions(
  departments: Department[] | undefined
): EntityPickerDepartmentFilterOption[] {
  if (!departments || departments.length === 0) return [];
  const sorted = [...departments].sort((a, b) => (a.path ?? a._id).localeCompare(b.path ?? b._id));
  return sorted.map((d) => ({
    id: d._id,
    label: d.name,
    depth: depthFromPath(d.path),
    pathLabels: getDepartmentPathNames(d.path, departments)
  }));
}

export function buildDepartmentFilter(
  t: TFunction,
  options: EntityPickerDepartmentFilterOption[],
  selectedId: string | null,
  onChange: (id: string | null) => void
): EntityPickerDepartmentFilter {
  return {
    field: 'department._id',
    options,
    selectedId,
    onChange,
    allLabel: t('entityPicker.common.departmentFilterAll'),
    prefixLabel: t('entityPicker.common.departmentFilterPrefix'),
    layout: 'rail'
  };
}

function depthFromPath(path: string | undefined): number {
  if (!path) return 0;
  // path is "<rootId>#...#<parentId>#<_id>". Depth is the number of ancestors.
  const parts = path.split('#').filter(Boolean);
  return Math.max(parts.length - 1, 0);
}
